import React, { createContext, useContext, useState } from 'react';
import { checkCognito, checkUser } from '../auth/AuthState';

type AuthProviderProps = {
  children: React.ReactNode;
};

export enum AuthType {
  LOGIN = 'login',
  REGISTER = 'register',
}

interface AuthContextProps {
  authFlow: AuthType;
  setAuthFlow: (authFlow: AuthType) => void;
  isAuth: boolean;
  isLoading: boolean;
  isCognitoAuth: boolean;
  login: () => Promise<void>;
  initialise: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps>({
  authFlow: AuthType.LOGIN,
  setAuthFlow: () => {},
  isAuth: false,
  isLoading: true,
  isCognitoAuth: false,
  login: async () => {},
  initialise: async () => {},
});

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [authFlow, setAuthFlow] = useState<AuthType>(AuthType.LOGIN);
  const [isAuth, setIsAuth] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCognitoAuth, setIsCognitoAuth] = useState<boolean>(false);

  const login = async (): Promise<void> => {
    setIsLoading(true);
    const auth = await checkUser();
    setIsAuth(auth);
    const cognitoAuth = await checkCognito();
    setIsCognitoAuth(cognitoAuth);
    setIsLoading(false);
  };

  const initialise = async (): Promise<void> => {
    try {
      if (!isAuth) {
        const auth = await checkUser();
        setIsAuth(auth);
      }
      if (!isCognitoAuth) {
        const cognitoAuth = await checkCognito();
        setIsCognitoAuth(cognitoAuth);
      }

      setIsLoading(false);
    } catch (e) {
      console.log(e);
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        authFlow,
        setAuthFlow,
        isAuth,
        isLoading,
        isCognitoAuth,
        login,
        initialise,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export { AuthProvider };
